// Motor de sincronización para los borradores locales de Evaluación de Inspección (ver plan
// "Captura offline de Evaluación de Inspección"). Se dispara automáticamente al recuperar
// señal, al montar la pantalla, y con un reintento de respaldo periódico -- nunca requiere que
// el inspector presione nada.
//
// Regla de conflictos ya acordada: quien tiene la OT asignada siempre gana. El payload local
// siempre se manda COMPLETO (nunca un parche parcial), y el backend (saveEvaluation +
// replaceHazardousSubstances/saveConcepts/saveTextFields) ya reemplaza todo por diseño, así
// que sincronizar = sobrescribir sin necesidad de resolver conflictos campo por campo.
import api from '../config/axiosConfig';
import inspectionEvaluationService from './inspectionEvaluationService';
import offlineEvaluationStore from './offlineEvaluationStore';
import { OFFLINE_FIELD_MODE } from '../config/features';

// El evento 'online' del navegador no es confiable (solo dice que el radio está prendido, no
// que haya internet real) -- este intervalo de respaldo reintenta igual mientras haya
// pendientes, para no depender solo de ese evento.
const RETRY_INTERVAL_MS = 25000;

let syncInProgress = false;
let syncPromise = null;
let retryTimer = null;
// Cuántas pantallas/componentes pidieron sincronización automática: el indicador global y la
// pantalla de evaluación la arrancan por separado; el temporizador se detiene solo cuando ya
// nadie la necesita (antes, salir de la evaluación apagaba la del resto de la app).
let autoSyncUsers = 0;
const statusListeners = new Set();

const notifyListeners = (status) => {
  statusListeners.forEach((cb) => {
    try { cb(status); } catch { /* un listener roto no debe tumbar a los demás */ }
  });
};

/** Suscribirse a cambios de estado de sync: { phase: 'idle'|'syncing'|'auth-required'|'error', detail? } */
function subscribe(callback) {
  statusListeners.add(callback);
  return () => statusListeners.delete(callback);
}

const isNetworkFailure = (error) => !error?.response;
const isAuthFailure = (error) => error?.response?.status === 401;
// 4xx que no se arreglan reintentando (403 sin permiso / ente no asignado, 404, 409, 400...).
// 408 y 429 sí son temporales.
const isPermanentFailure = (error) => {
  const status = error?.response?.status;
  return status >= 400 && status < 500 && ![401, 408, 429].includes(status);
};
// Rechazo armado por el propio motor (no por el servidor), con la forma de un error de axios
const syntheticRejection = (status, code, message, details = null) => {
  const error = new Error(message);
  error.response = { status, data: { error: message, code, details } };
  return error;
};

const currentUserId = () => {
  try {
    return JSON.parse(sessionStorage.getItem('userInfo') || '{}').id ?? null;
  } catch {
    return null;
  }
};

const rejectionMessage = (error) => {
  const data = error?.response?.data;
  const serverMessage = typeof data === 'string' ? data : (data?.error || data?.message);
  if (serverMessage) return serverMessage;
  return error?.response?.status === 403
    ? 'No tienes permiso para guardar esta evaluación (el ente ya no está asignado a tu usuario).'
    : 'El servidor rechazó los cambios guardados en este dispositivo.';
};

// Une dos listas de sustancias sin perder ninguna: al recuperar un borrador sobre una evaluación
// recién creada, las heredadas de la cotización se conservan aunque el borrador no las traiga.
const substanceKey = (s) => `${(s?.substanceName || '').trim().toLowerCase()}|${(s?.unit || '').trim().toLowerCase()}`;
function mergeHazardousSubstances(primary = [], extra = []) {
  const merged = [...(primary || [])];
  const seen = new Set(merged.map(substanceKey));
  (extra || []).forEach((s) => {
    if (!s?.substanceName || seen.has(substanceKey(s))) return;
    seen.add(substanceKey(s));
    const { id, ...rest } = s;
    merged.push(rest);
  });
  return merged;
}

// Diferencias reales de contenido entre la versión del sistema y el borrador del dispositivo
// (conceptos, campos de texto, sustancias y datos de sitio). Si no hay ninguna, no hay nada que
// decidir aunque la versión haya cambiado.
const norm = (v) => (v == null ? '' : String(v).trim());
const conceptMap = (list) => new Map((list || []).filter((c) => c?.estado)
  .map((c) => [`${c.sectionType}|${c.conceptKey}`, `${norm(c.estado)}|${norm(c.observaciones)}`]));
const textMap = (fields) => {
  const map = new Map();
  Object.entries(fields || {}).forEach(([section, values]) => Object.entries(values || {}).forEach(([key, value]) => {
    if (norm(value)) map.set(`${section}|${key}`, norm(value));
  }));
  return map;
};
const substanceMap = (list, none) => (none ? new Map() : new Map((list || []).filter((s) => s?.substanceName)
  .map((s) => [substanceKey(s), String(Number(s.quantity))])));
const countDiff = (a, b) => [...new Set([...a.keys(), ...b.keys()])].filter((k) => a.get(k) !== b.get(k)).length;
const SITE_FIELDS = ['civilProtectionApproval', 'civilProtectionApprovalDate', 'civilProtectionApprovalValidity'];

function diffEvaluation(server = {}, local = {}) {
  return {
    concepts: countDiff(conceptMap(server.concepts), conceptMap(local.concepts)),
    textFields: countDiff(textMap(server.textFields), textMap(local.textFields)),
    substances: countDiff(substanceMap(server.hazardousSubstances, server.noHazardousSubstances),
      substanceMap(local.hazardousSubstances, local.noHazardousSubstances)),
    siteData: SITE_FIELDS.filter((f) => norm(server[f]) !== norm(local[f])).length
      + (!!server.noHazardousSubstances !== !!local.noHazardousSubstances ? 1 : 0)
  };
}
const hasDifferences = (diff) => Object.values(diff).some((n) => n > 0);

/**
 * Conflicto: la copia de este borrador salió del servidor y desde entonces la evaluación cambió
 * (número de versión distinto; los borradores viejos sin versión comparan la hora). Solo se le
 * pregunta al inspector si OTRA persona la guardó y el contenido es distinto -- un reinicio del
 * servidor, otra pestaña propia o un guardado idéntico se resuelven solos. Lanza un rechazo
 * 404/409 sintético.
 */
async function checkServerConflict(draft, authConfig = { skipAuthRedirect: true }) {
  if (!draft?.evaluationId || draft.forceOverwrite) return;
  if (draft.serverVersion == null && !draft.serverUpdatedAt) return;
  let server;
  try {
    server = await inspectionEvaluationService.getById(draft.evaluationId, authConfig);
  } catch (error) {
    if (error?.response?.status === 404) {
      throw syntheticRejection(404, 'EVALUATION_NOT_FOUND',
        'La evaluación de este borrador ya no existe en el sistema.');
    }
    throw error;
  }
  const changed = draft.serverVersion != null && server?.version != null
    ? Number(server.version) !== Number(draft.serverVersion)
    : !!(server?.updatedAt && draft.serverUpdatedAt && new Date(server.updatedAt) > new Date(draft.serverUpdatedAt));
  if (!changed) return;

  const diff = diffEvaluation(server, draft.data || {});
  if (!hasDifferences(diff)) return;
  const me = currentUserId();
  if (server.updatedBy != null && me != null && String(server.updatedBy) === String(me)) return;

  throw syntheticRejection(409, 'CONFLICT',
    'La evaluación cambió en el sistema después de tu última sincronización.',
    { serverUpdatedAt: server.updatedAt || null, serverUpdatedByName: server.updatedByName || null, diff });
}

/**
 * Reproduce, para UN borrador, el mismo flujo que ya usa la pantalla online:
 * - Si no se conoce evaluationId: existsByEntity -> si existe, se toma ese id; si no, se crea
 *   con createFromQuotation (mismo payload que hoy arma loadEvaluationForEntity).
 * - Con el id ya resuelto, save() manda el payload COMPLETO del borrador -- así "sobrescribe
 *   todo" aplica igual haya sido creación o actualización.
 */
async function syncDraft(draft) {
  const { quotationId, branchId, data } = draft;
  const authConfig = { skipAuthRedirect: true };
  let evaluationId = draft.evaluationId;
  let inheritedSubstances = [];

  if (!evaluationId) {
    const exists = await inspectionEvaluationService.existsByEntity(quotationId, branchId, authConfig);
    if (exists) {
      const existing = await inspectionEvaluationService.getByEntity(quotationId, branchId, authConfig);
      evaluationId = existing.id;
    } else {
      let companyId = data._createPayload?.companyId;
      if (!companyId) {
        const quotationResponse = await api.get(`/quotations/${quotationId}`, authConfig);
        companyId = quotationResponse.data.company?.id || quotationResponse.data.companyId;
      }
      const created = await inspectionEvaluationService.createFromQuotation(
        quotationId,
        {
          companyId,
          branchId,
          evaluatorName: data._createPayload?.evaluatorName,
          createdBy: data._createPayload?.createdBy
        },
        authConfig
      );
      evaluationId = created.id;
      inheritedSubstances = created.hazardousSubstances || [];
    }
  }

  await checkServerConflict(draft, authConfig);

  // El payload de "save" no lleva el _createPayload (es metadata solo para el paso de arriba).
  const { _createPayload, ...savePayload } = data;
  const finalPayload = { ...savePayload, id: evaluationId, quotationId, branchId };
  if (!savePayload.noHazardousSubstances && inheritedSubstances.length) {
    finalPayload.hazardousSubstances = mergeHazardousSubstances(savePayload.hazardousSubstances, inheritedSubstances);
  }
  return inspectionEvaluationService.save(finalPayload, authConfig);
}

/**
 * Sincroniza todos los borradores pendientes. Protegido por `syncInProgress` para que el
 * evento 'online' y el reintento periódico nunca se disparen en paralelo.
 */
function syncAllPending() {
  if (!syncPromise) {
    syncPromise = runSyncAllPending().finally(() => { syncPromise = null; });
  }
  return syncPromise;
}

/** Resuelve cuando no hay una sincronización en curso (la pantalla espera antes de guardar). */
const waitForIdle = () => (syncPromise || Promise.resolve()).catch(() => {});

async function runSyncAllPending() {
  if (syncInProgress) return;
  const pending = await offlineEvaluationStore.listPendingDrafts();
  if (pending.length === 0) return;

  syncInProgress = true;
  notifyListeners({ phase: 'syncing' });

  for (const draft of pending) {
    try {
      const saved = await syncDraft(draft);
      // Si el inspector guardó otra vez mientras esto viajaba, su copia nueva sigue pendiente
      // (no se pisa con la que se acaba de subir); solo se le apunta el id y la versión.
      const current = await offlineEvaluationStore.getDraft(draft.quotationId, draft.branchId);
      const changedMeanwhile = !!current && current.lastLocalChangeAt !== draft.lastLocalChangeAt;
      await offlineEvaluationStore.saveDraft(draft.quotationId, draft.branchId,
        changedMeanwhile ? current.data : draft.data, {
          evaluationId: saved.id,
          pendingSync: changedMeanwhile,
          serverUpdatedAt: saved.updatedAt || null,
          serverVersion: saved.version ?? null,
          forceOverwrite: false
        });
      notifyListeners({ phase: 'synced', detail: { ...draft, evaluationId: saved.id, savedHazardousSubstances: saved.hazardousSubstances } });
    } catch (error) {
      if (isAuthFailure(error)) {
        // Sesión vencida mientras estaba offline -- el borrador se queda intacto y marcado
        // como pendiente; se avisa para que el inspector vuelva a iniciar sesión, y el
        // reintento periódico lo recogerá solo después de eso.
        notifyListeners({ phase: 'auth-required', detail: draft });
      } else if (isPermanentFailure(error)) {
        // Reintentar no lo arregla (antes: un 403 cada 25 s para siempre). Se deja de enviar,
        // los datos se quedan en el dispositivo y se avisa en la pantalla de ese ente.
        const rejection = {
          status: error.response.status, message: rejectionMessage(error),
          code: error.response.data?.code || null, details: error.response.data?.details || null
        };
        await offlineEvaluationStore.markSyncRejected(draft.quotationId, draft.branchId, rejection);
        console.warn('Borrador offline rechazado por el servidor; no se reintentará:', draft.quotationId, draft.branchId, rejection);
        notifyListeners({ phase: 'rejected', detail: { ...draft, syncRejected: rejection } });
      } else if (isNetworkFailure(error)) {
        // Se cayó la señal a media sincronización -- se reintenta en el próximo disparo,
        // el borrador ya quedó marcado pendingSync=true desde que se escribió localmente.
        notifyListeners({ phase: 'idle' });
      } else {
        console.error('Error real (no de red) sincronizando borrador offline:', error);
        notifyListeners({ phase: 'error', detail: error });
      }
    }
  }

  syncInProgress = false;
  notifyListeners({ phase: 'idle' });
}


function startAutoSync() {
  if (!OFFLINE_FIELD_MODE || typeof window === 'undefined') return () => {};

  autoSyncUsers += 1;
  if (autoSyncUsers === 1) {
    window.addEventListener('online', syncAllPending);
    retryTimer = setInterval(syncAllPending, RETRY_INTERVAL_MS);
  }
  if (navigator.onLine) {
    syncAllPending();
  }

  let stopped = false;
  return () => {
    if (stopped) return;
    stopped = true;
    autoSyncUsers = Math.max(0, autoSyncUsers - 1);
    if (autoSyncUsers === 0) {
      window.removeEventListener('online', syncAllPending);
      if (retryTimer) {
        clearInterval(retryTimer);
        retryTimer = null;
      }
    }
  };
}

const offlineSyncEngine = {
  subscribe,
  syncAllPending,
  waitForIdle,
  mergeHazardousSubstances,
  diffEvaluation,
  startAutoSync,
  checkServerConflict,
  isNetworkFailure,
  isAuthFailure,
  isPermanentFailure
};

export default offlineSyncEngine;
